import InsuranceService from "../../services/InsuranceService/InsuranceService.ts";
import { useEffect, useState } from "react";
import { useAuth } from "../../provider/AuthProvider.tsx";
import SelectGroup from "../../components/Forms/SelectGroup/SelectGroup.tsx";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb.tsx";
import FadeAlert, {
  FadeAlertType,
} from "../../components/FadeAlert/FadeAlert.tsx";
import { normalizeNewPolicyProperties } from "../../utils/NormalizationUtil.ts";
import PolicyTypeSelect from "../../components/Forms/PolicyTypeSelect.tsx";
import SectionHeader from "../../components/SectionHeader/SectionHeader.tsx";
import { inputClass, labelClass } from "../../constants/formStyles";

const NEW_POLICY_ENTRY_OPTION = { "0": "Нова полица" };
const NEW_POLICY_VALUE = "0";

interface NestedOptions {
  [groupLabel: string]: { [optionValue: string]: string };
}

const PoliciesSettingsPage = () => {
  const [policies, setPolicies] = useState<{ [key: string]: string }>({});
  const [basePolicies, setBasePolicies] = useState<NestedOptions>({});
  const [error, setError] = useState<string | null>(null);
  const [selectedPolicy, setSelectedPolicy] = useState<string>("");
  const [selectedBasePolicy, setSelectedBasePolicy] = useState<string>("");
  const [alert, setAlert] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<FadeAlertType | null>(null);
  const [newPolicyName, setNewPolicyName] = useState<string>("");

  const { jwt } = useAuth();

  const fetchData = async (): Promise<void> => {
    try {
      const policyTypes = await InsuranceService.getAllPolicyTypes(jwt);
      const basePoliciesData = await InsuranceService.getBasePolicies(jwt);
      setPolicies({
        ...NEW_POLICY_ENTRY_OPTION,
        ...(policyTypes as { [key: string]: string }),
      });
      setBasePolicies(basePoliciesData as NestedOptions);
    } catch (err) {
      setError("Грешка при зареждане на данните за полиците.");
    }
  };

  useEffect(() => {
    const fetchPoliciesByBasePolicyType = async () => {
      if (selectedBasePolicy) {
        try {
          const policyTypesByBasePolicy =
            await InsuranceService.getAllPolicyTypes(jwt, selectedBasePolicy);
          setPolicies({
            ...NEW_POLICY_ENTRY_OPTION,
            ...(policyTypesByBasePolicy as { [key: string]: string }),
          });
        } catch (err) {
          setError("Грешка при зареждане на типове полици по база.");
        }
      } else {
        fetchData();
      }
    };
    fetchPoliciesByBasePolicyType();
  }, [selectedBasePolicy, jwt]);

  useEffect(() => {
    fetchData();
  }, [jwt]);

  const displayAlert = (message: string, type: FadeAlertType): void => {
    setAlert(message);
    setAlertType(type);
  };

  const handlePolicyChange = async (value: string) => {
    try {
      if (value === NEW_POLICY_VALUE) {
        setNewPolicyName("");
        setSelectedPolicy(value);
      } else {
        const policyData = await InsuranceService.getPolicyDataByPolicyType(
          jwt,
          value
        );
        setSelectedPolicy(value);
        if (policyData.baseType && basePolicies[policyData.baseType]) {
          setSelectedBasePolicy(policyData.baseType);
        } else {
          setSelectedBasePolicy("");
        }
      }
    } catch (error) {
      setError("Грешка при зареждане на детайли за полица.");
    }
  };

  const handleBasePolicyChange = (value: string) => {
    setSelectedBasePolicy(value);
    if (Number(selectedPolicy) !== 0) setSelectedPolicy("");
  };

  const handlePolicySubmit = async (): Promise<void> => {
    try {
      const policyName =
        newPolicyName !== "" ? newPolicyName : policies[selectedPolicy];
      const policyIdToSubmit =
        selectedPolicy === NEW_POLICY_VALUE ? null : Number(selectedPolicy);
      const policyToSubmit = {
        name: policyName,
        policyId: policyIdToSubmit,
        baseType: Number(selectedBasePolicy) ?? null,
      };
      await InsuranceService.writePolicyData(
        jwt,
        normalizeNewPolicyProperties(policyToSubmit, true)
      );
      displayAlert("Успешно записан тип на полица", FadeAlertType.success);
      await fetchData();
      setSelectedPolicy("");
      setSelectedBasePolicy("");
      setNewPolicyName("");
    } catch (err: any) {
      displayAlert(
        "Грешка при записването на полица. Моля, опитайте отново.",
        FadeAlertType.error
      );
    }
  };

  return (
    <>
      <Breadcrumb pageName="Настройки на Полици" />
      <div className="border-stroke shadow-default dark:border-strokedark dark:bg-boxdark mt-2 rounded-sm border bg-white">
        <FadeAlert
          alert={alert}
          type={alertType}
          onClose={() => setAlert(null)}
        />

        <SectionHeader title="Управление на Полиците" />

        <div className="p-3">
          {error && (
            <FadeAlert
              alert={error}
              type={FadeAlertType.error}
              onClose={() => setError(null)}
            />
          )}

          <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
            <div>
              <PolicyTypeSelect
                options={basePolicies}
                value={selectedBasePolicy}
                onChange={handleBasePolicyChange}
                name="basePolicyType"
                placeholder="Изберете Базов Тип на Полицата"
              />
            </div>
            <div>
              <label className={labelClass}>Вид полица</label>
              <SelectGroup
                value={selectedPolicy}
                placeholder="Изберете Вид Полица"
                options={policies}
                onChange={handlePolicyChange}
              />
            </div>
            {selectedPolicy === NEW_POLICY_VALUE && (
              <div>
                <label className={labelClass}>Име на новият вид полица</label>
                <input
                  type="text"
                  value={newPolicyName}
                  onChange={(e) => setNewPolicyName(e.target.value)}
                  className={inputClass}
                />
              </div>
            )}
            <div className="flex items-end">
              <button
                className="bg-primary w-full justify-center rounded px-3 py-1.5 text-xs font-medium text-white hover:bg-opacity-90 disabled:opacity-50"
                onClick={handlePolicySubmit}
                disabled={!selectedPolicy && !newPolicyName}
              >
                Запис
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PoliciesSettingsPage;
